// src/components/ErrorHandler.jsx
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

const ErrorHandler = ({
  statusCode,
  message,
  onRetry,
  onGoHome,
  onGoBack,
  showDetails,
  errorDetails,
}) => {
  const [errorConfig, setErrorConfig] = useState({
    title: "An Error Occurred",
    message: "Something went wrong. Please try again later.",
    color: "red",
    icon: "❌",
  });

  useEffect(() => {
    const configs = {
      204: {
        title: "No Data Available",
        message:
          "There is no data to display right now. Try adjusting filters or come back later.",
        color: "emerald",
        icon: "📭",
      },
      400: {
        title: "Bad Request",
        message: "The server cannot process the request due to invalid syntax.",
        color: "orange",
        icon: "⚠️",
      },
      401: {
        title: "Unauthorized",
        message: "You need to be authenticated to access this resource.",
        color: "amber",
        icon: "🔒",
      },
      403: {
        title: "Forbidden",
        message: "You do not have permission to access this resource.",
        color: "red",
        icon: "🚫",
      },
      404: {
        title: "Not Found",
        message: "The requested resource could not be found on this server.",
        color: "purple",
        icon: "🔍",
      },
      500: {
        title: "Internal Server Error",
        message: "The server encountered an unexpected condition.",
        color: "red",
        icon: "💥",
      },
      502: {
        title: "Bad Gateway",
        message:
          "The server received an invalid response from the upstream server.",
        color: "blue",
        icon: "🔌",
      },
      503: {
        title: "Service Unavailable",
        message: "The server is currently unavailable. Please try again later.",
        color: "yellow",
        icon: "⏳",
      },
      504: {
        title: "Gateway Timeout",
        message:
          "The server did not receive a timely response from the upstream server.",
        color: "indigo",
        icon: "⏰",
      },
    };

    if (statusCode && configs[statusCode]) {
      setErrorConfig({
        ...configs[statusCode],
        message: message || configs[statusCode].message,
      });
    } else {
      setErrorConfig({
        title: "Unknown Error",
        message: message || "An unexpected error occurred.",
        color: "gray",
        icon: "❓",
      });
    }
  }, [statusCode, message]);

  const colorClasses = {
    red: {
      bg: "bg-red-50",
      text: "text-red-800",
      iconBg: "bg-red-500",
      button: "bg-red-600 hover:bg-red-700",
    },
    orange: {
      bg: "bg-orange-50",
      text: "text-orange-800",
      iconBg: "bg-orange-500",
      button: "bg-orange-600 hover:bg-orange-700",
    },
    amber: {
      bg: "bg-amber-50",
      text: "text-amber-800",
      iconBg: "bg-amber-500",
      button: "bg-amber-600 hover:bg-amber-700",
    },
    yellow: {
      bg: "bg-yellow-50",
      text: "text-yellow-800",
      iconBg: "bg-yellow-500",
      button: "bg-yellow-600 hover:bg-yellow-700",
    },
    purple: {
      bg: "bg-purple-50",
      text: "text-purple-800",
      iconBg: "bg-purple-500",
      button: "bg-purple-600 hover:bg-purple-700",
    },
    blue: {
      bg: "bg-blue-50",
      text: "text-blue-800",
      iconBg: "bg-blue-500",
      button: "bg-blue-600 hover:bg-blue-700",
    },
    indigo: {
      bg: "bg-indigo-50",
      text: "text-indigo-800",
      iconBg: "bg-indigo-500",
      button: "bg-indigo-600 hover:bg-indigo-700",
    },
    emerald: {
      bg: "bg-emerald-50",
      text: "text-emerald-800",
      iconBg: "bg-emerald-500",
      button: "bg-emerald-600 hover:bg-emerald-700",
    },
    gray: {
      bg: "bg-gray-50",
      text: "text-gray-800",
      iconBg: "bg-gray-500",
      button: "bg-gray-600 hover:bg-gray-700",
    },
  };

  const colors = colorClasses[errorConfig.color] || colorClasses.red;

  return (
    <div
      className={`${colors.bg} border border-gray-200 rounded-2xl shadow-sm p-8 max-w-2xl mx-auto my-8`}
    >
      <div className="flex flex-col items-center text-center">
        <div
          className={`${colors.iconBg} w-24 h-24 rounded-full flex items-center justify-center mb-6`}
        >
          <span className="text-4xl">{errorConfig.icon}</span>
        </div>

        <h1 className={`${colors.text} text-2xl font-bold mb-4`}>
          {errorConfig.title}
        </h1>

        <p className="text-gray-600 text-base mb-8 leading-relaxed">
          {errorConfig.message}
        </p>

        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {onRetry && (
            <button
              onClick={onRetry}
              className={`${colors.button} text-white font-medium py-2.5 px-5 rounded-lg transition-colors duration-200`}
            >
              {statusCode === 204 ? "Refresh Data" : "Try Again"}
            </button>
          )}

          {onGoBack && (
            <button
              onClick={onGoBack}
              className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors duration-200"
            >
              Go Back
            </button>
          )}

          {onGoHome && (
            <button
              onClick={onGoHome}
              className="bg-gray-800 hover:bg-black text-white font-medium py-2.5 px-5 rounded-lg transition-colors duration-200"
            >
              Go to Homepage
            </button>
          )}
        </div>

        {showDetails && errorDetails && (
          <details className="w-full max-w-lg mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
            <summary
              className={`${colors.button} text-white font-medium p-3 cursor-pointer list-none`}
            >
              Technical Details
            </summary>
            <div className="p-4">
              <pre className="text-sm text-gray-700 bg-gray-50 p-3 rounded overflow-x-auto whitespace-pre-wrap">
                {typeof errorDetails === "object"
                  ? JSON.stringify(errorDetails, null, 2)
                  : errorDetails}
              </pre>
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

ErrorHandler.propTypes = {
  statusCode: PropTypes.number,
  message: PropTypes.string,
  onRetry: PropTypes.func,
  onGoHome: PropTypes.func,
  onGoBack: PropTypes.func,
  showDetails: PropTypes.bool,
  errorDetails: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

ErrorHandler.defaultProps = {
  showDetails: false,
};

export default ErrorHandler;
